import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import {
  getProvisionStatus,
  provisionSpectrePOS,
  stopProvisionPolling,
} from "./plugin.redux";
import { openChentroShop } from "./SpectreposStatusPanel";

const STEPS = [
  { key: "creating", label: "provision.step.creating_store" },
  { key: "configuring", label: "provision.step.configuring" },
  { key: "credentials", label: "provision.step.generating_keys" },
  { key: "syncing", label: "provision.step.importing_catalog" },
  { key: "ready", label: "provision.step.ready" },
];

// A shop on Chentro has no WooCommerce to configure and no API keys: it is
// created, then the bowling catalog comes in.
const HBS_STEPS = [
  { key: "creating", label: "provision.step.creating_store" },
  { key: "syncing", label: "provision.step.importing_catalog" },
  { key: "ready", label: "provision.step.ready" },
];

const getHbsStepIndex = (status) => {
  if (status === "syncing" || status === "syncing_catalog") return 1;
  if (status === "ready") return 2;
  return 0;
};

const getStepIndex = (status) => {
  if (!status || status === "provisioning") return 0;
  if (status === "created" || status === "configuring") return 1;
  if (status === "credentials" || status === "generating_keys") return 2;
  if (status === "syncing" || status === "syncing_catalog") return 3;
  if (status === "ready") return 4;
  return 0;
};

const ProvisioningProgress = ({
  provisionStatus,
  provisionProgress,
  provisionBackend,
  getProvisionStatus,
  provisionSpectrePOS,
  stopProvisionPolling,
  compact,
}) => {
  const { t } = useTranslation();
  const [openFailed, setOpenFailed] = useState(false);
  const hbs = provisionBackend === "hbs";
  const steps = hbs ? HBS_STEPS : STEPS;
  const currentStep = hbs
    ? getHbsStepIndex(provisionStatus)
    : getStepIndex(provisionStatus);
  const syncStep = hbs ? 1 : 3;

  useEffect(() => {
    if (
      provisionStatus &&
      provisionStatus !== "ready" &&
      provisionStatus !== "error"
    ) {
      getProvisionStatus();
    }
    return () => {
      stopProvisionPolling();
    };
  }, []);

  if (provisionStatus === "ready") {
    return (
      <Container compact={compact}>
        <DoneMessage>
          <CheckIcon>✓</CheckIcon>
          {t("provision.store_ready")}
        </DoneMessage>
        {hbs && (
          <Actions>
            <ActionButton
              type="button"
              onClick={async () => setOpenFailed(!(await openChentroShop()))}
            >
              {t("provision.open_store", "Open my store")}
            </ActionButton>
            <ActionButton
              type="button"
              secondary
              onClick={async () =>
                setOpenFailed(!(await openChentroShop("/pos/register")))
              }
            >
              {t("provision.open_register", "Open the register")}
            </ActionButton>
          </Actions>
        )}
        {openFailed && (
          <HintText compact={compact}>
            {t(
              "provision.open_failed",
              "We couldn't open your store. Try again in a moment.",
            )}
          </HintText>
        )}
      </Container>
    );
  }

  if (provisionStatus === "error") {
    return (
      <Container compact={compact}>
        <ErrorMessage>{t("provision.error")}</ErrorMessage>
        {/* Provisioning is idempotent per user: trying again finishes the
            same shop, it never makes a second one. */}
        <Actions>
          <ActionButton type="button" onClick={() => provisionSpectrePOS()}>
            {t("provision.try_again", "Try again")}
          </ActionButton>
        </Actions>
      </Container>
    );
  }

  const syncProgress = provisionProgress;
  const syncCurrent = Number(syncProgress?.current ?? 0);
  const syncTotal = Number(syncProgress?.total ?? 0);
  const syncPercent =
    syncTotal > 0 ? Math.round((syncCurrent / syncTotal) * 100) : 0;

  return (
    <Container compact={compact}>
      <Title>{t("provision.setting_up_store")}</Title>
      <StepsContainer>
        {steps.map((step, idx) => {
          const isDone = idx < currentStep;
          const isActive = idx === currentStep;
          return (
            <StepRow key={step.key}>
              <StepIndicator done={isDone} active={isActive}>
                {isDone ? "✓" : idx + 1}
              </StepIndicator>
              <StepLabel done={isDone} active={isActive}>
                {t(step.label)}
              </StepLabel>
              {isActive && <PulsingDot />}
            </StepRow>
          );
        })}
      </StepsContainer>

      {hbs && currentStep === syncStep && syncProgress?.products > 0 && (
        <ProgressText>
          {t("provision.products_imported", {
            count: Number(syncProgress.products),
            defaultValue: "{{count}} products imported",
          })}
        </ProgressText>
      )}

      {!hbs && currentStep === 3 && syncProgress && (
        <ProgressBarContainer>
          <ProgressBarTrack>
            <ProgressBarFill percent={syncPercent} />
          </ProgressBarTrack>
          <ProgressText>
            {syncProgress.step || ""} — {syncPercent}%
            {syncCurrent > 0 && ` (${syncCurrent}/${syncTotal})`}
          </ProgressText>
        </ProgressBarContainer>
      )}

      <HintText>{t("provision.hint_can_leave")}</HintText>
    </Container>
  );
};

const Container = styled.div`
  padding: ${(props) => (props.compact ? "8px 4px" : "24px")};
  text-align: center;
`;

const Title = styled.h4`
  margin-bottom: ${(props) => (props.compact ? "10px" : "20px")};
  font-weight: 600;
  font-size: ${(props) => (props.compact ? "14px" : "16px")};
  color: #333;
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.compact ? "6px" : "12px")};
  max-width: 360px;
  margin: 0 auto;
  text-align: left;
`;

const StepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StepIndicator = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  background: ${(props) =>
    props.done ? "#28a745" : props.active ? "#17A2B8" : "#e9ecef"};
  color: ${(props) => (props.done || props.active ? "#fff" : "#6c757d")};
  transition: all 0.3s ease;
`;

const StepLabel = styled.span`
  font-size: 14px;
  font-weight: ${(props) => (props.active ? 600 : 400)};
  color: ${(props) =>
    props.done ? "#28a745" : props.active ? "#17A2B8" : "#adb5bd"};
  transition: all 0.3s ease;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const PulsingDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #17a2b8;
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const ProgressBarContainer = styled.div`
  margin: ${(props) => (props.compact ? "10px auto" : "20px auto")};
  max-width: 360px;
`;

const ProgressBarTrack = styled.div`
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  width: ${(props) => props.percent || 0}%;
  background: linear-gradient(90deg, #17a2b8, #28a745);
  border-radius: 4px;
  transition: width 0.5s ease;
`;

const ProgressText = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 6px;
  text-align: center;
`;

const HintText = styled.p`
  margin-top: ${(props) => (props.compact ? "8px" : "20px")};
  margin-bottom: 0;
  font-size: ${(props) => (props.compact ? "11px" : "13px")};
  line-height: 1.35;
  color: #6c757d;
  font-style: italic;
`;

const DoneMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #28a745;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const ActionButton = styled.button`
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid #0e7a4d;
  color: ${(props) => (props.secondary ? "#0e7a4d" : "#fff")};
  background: ${(props) => (props.secondary ? "#fff" : "#0e7a4d")};
  cursor: pointer;
`;

const CheckIcon = styled.span`
  font-size: 20px;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-weight: 600;
  font-size: 14px;
`;

export default connect(
  (state) => ({
    provisionStatus: state.plugin.provisionStatus,
    provisionProgress: state.plugin.provisionProgress,
    provisionBackend: state.plugin.provisionBackend,
  }),
  {
    getProvisionStatus,
    provisionSpectrePOS,
    stopProvisionPolling,
  },
)(ProvisioningProgress);
